///////////////////////////////////////////
// Shared Types (used by both API and UI)
///////////////////////////////////////////

/**
 * HTML template unique identifier.
 */
export type HtmlTemplateId = string;

/**
 * Template type identifier (e.g., packing slip, shipping label).
 */
export const TemplateType = {
  PackingSlip: 'packingSlip',
  PickList: 'pickList',
} as const;

export type TemplateType = (typeof TemplateType)[keyof typeof TemplateType];

/**
 * Page size for printed documents.
 */
export const Layout = {
  /** 4" x 6" label size */
  FourBySix: 1,
  /** 8.5" x 11" letter size */
  EightAndHalfByEleven: 2,
  A4: 3,
  A6: 4,
} as const;

export type Layout = (typeof Layout)[keyof typeof Layout];

/**
 * Page orientation for printed documents.
 */
export const Orientation = {
  Portrait: 1,
  Landscape: 2,
} as const;

export type Orientation = (typeof Orientation)[keyof typeof Orientation];

///////////////////////////////////////////
// API Types
///////////////////////////////////////////

/**
 * Schema for a single property in the template model.
 *
 * Can represent:
 * - Primitives: string, number, integer, boolean
 * - Objects: nested properties via `properties`
 * - Arrays: item schema via `itemSchema`
 *
 * Properties:
 * - `type`: JSON type of the property
 * - `translationKey`: Translation key for UI display (e.g., "orderNumber", "postalCode")
 * - `properties`: For objects - nested property schemas
 * - `itemSchema`: For arrays - schema of each array element
 */
export type PropertySchema = {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array';
  translationKey: string;
  description?: string;
  properties?: Record<string, PropertySchema>;
  itemSchema?: PropertySchema;
};

/**
 * Hierarchical JSON Schema representing the template model structure.
 *
 * Root is always an object containing top-level properties.
 *
 * Properties:
 * - `type`: Always "object" for root schema
 * - `properties`: Dictionary of property schemas keyed by JSON property name
 */
export type TemplateSchema = {
  type: 'object';
  properties: Record<string, PropertySchema>;
};

/**
 * API response containing available template fields for field replacement.
 *
 * Properties:
 * - `schema`: Hierarchical schema describing the template model structure
 * - `sampleData`: Sample data matching the template structure, used for preview/testing.
 *   Structure varies by template type (e.g., PackingSlipModel with order, items, billing, etc.)
 */
export type HtmlTemplateConfig = {
  schema: TemplateSchema;
  sampleData: unknown;
};

/**
 * HTML template returned from the API.
 *
 * Always includes an ID as templates are persisted in the database.
 * When creating/updating templates, the ID is passed in the URL, not in the request body.
 *
 * Properties:
 * - `id`: Unique template identifier
 * - `packingSlipId`: Database primary key (INT) for the bridge PackingSlip record
 * - `name`: User-defined template name
 * - `html`: Liquid HTML template content with field tokens
 * - `layout`: Page size (4x6 or 8.5x11)
 * - `orientation`: Portrait or landscape
 */
export type HtmlTemplate = {
  id: HtmlTemplateId;
  packingSlipId?: number;
  name: string;
  html: string;
  layout: Layout;
  orientation: Orientation;
  createDate?: string;
  modifyDate?: string;
  modifiedBy?: string;
};

/**
 * API response containing shared (ShipStation-provided) and customer (user-created) HTML templates.
 *
 * Properties:
 * - `shared`: ShipStation default templates available to all users
 * - `customer`: User-created custom templates
 */
export type HtmlTemplatesResponse = {
  shared: HtmlTemplate[];
  customer: HtmlTemplate[];
};

/**
 * Request body for creating or updating HTML templates.
 *
 * Used when sending template data to the API (POST/PUT).
 * Template ID is passed separately in the URL path, not in the request body.
 *
 * Properties:
 * - `name`: User-defined template name (min 3 characters)
 * - `html`: Liquid HTML template content with field tokens
 * - `layout`: Page size (4x6 or 8.5x11)
 * - `orientation`: Portrait or landscape
 * - `settings`: Optional template-type-specific settings
 */
export type HtmlTemplateRequest = {
  name: string;
  html: string;
  layout: Layout;
  orientation: Orientation;
  settings?: Record<string, unknown>;
};

/**
 * API response when saving or updating an HTML template.
 *
 * Properties:
 * - `id`: The template-type-specific identifier of the saved template.
 *   For packing slips this is the packingSlipId (as string).
 */
export type SaveTemplateResult = {
  id: string;
};

export const PreviewTrigger = {
  Automatic: 'automatic',
  Manual: 'manual',
} as const;

export type PreviewTrigger = (typeof PreviewTrigger)[keyof typeof PreviewTrigger];

export const ErrorType = {
  Api: 'api',
  HtmlValidation: 'htmlValidation',
  TemplateNameValidation: 'templateNameValidation',
} as const;

export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];

export const SettingType = {
  Layout: 'layout',
  Orientation: 'orientation',
  Name: 'name',
  DarkMode: 'darkMode',
} as const;

export type SettingType = (typeof SettingType)[keyof typeof SettingType];

export type TrackWorkflowFunction = (workflowStep: string, metadata?: Record<string, unknown>) => Record<string, unknown>;

export const ApiValidationErrorType = {
  FileSizeTooLarge: 'file_size_too_large',
  UnsupportedImageType: 'unsupported_image_type',
  InvalidUrl: 'invalid_url',
  BlockedHost: 'blocked_host',
} as const;

export type ApiValidationErrorType = (typeof ApiValidationErrorType)[keyof typeof ApiValidationErrorType];

export type ApiError = {
  detail: string;
  errorType: ApiValidationErrorType;
};

export type ApiValidationError = {
  message: string;
  errors: ApiError[];
};

export const ValidationErrorType = {
  Syntax: 'syntax',
  MalformedLiquid: 'malformed-liquid',
  UnsupportedElement: 'unsupported-element',
  UnsupportedAttribute: 'unsupported-attribute',
  InvalidField: 'invalid-field',
  ImageFileSizeTooLarge: 'image-file-size-too-large',
  ImageUnsupportedType: 'image-unsupported-type',
  ImageInvalidUrl: 'image-invalid-url',
  ImageBlockedHost: 'image-blocked-host',
} as const;

export type ValidationErrorType = (typeof ValidationErrorType)[keyof typeof ValidationErrorType];

export type HtmlValidationError = {
  line: number;
  type: ValidationErrorType;
  detail?: string;
};

export type ApiErrorDetails = {
  errorType: ValidationErrorType;
  detail: string;
};
